import { useNavigate } from 'react-router-dom';
import {
  SUPPORT_PHONE_DISPLAY,
  SUPPORT_PHONE_TEL,
  LEGAL_OPERATOR_NAME,
  LEGAL_ADDRESS_STREET,
  LEGAL_ADDRESS_CITY,
  LEGAL_ADDRESS_COUNTRY,
} from '../constants/contact';

export default function AVV() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center gap-4">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-800 transition-colors"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M12 5l-7 7 7 7" />
            </svg>
            Zurück
          </button>
          <span className="text-gray-300">|</span>
          <span className="text-sm font-semibold text-gray-700" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            Candlear Booking
          </span>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-6 py-12">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 md:p-12">

          <h1 className="text-3xl font-bold text-gray-900 mb-1" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            Auftragsverarbeitungsvertrag
          </h1>
          <p className="text-base text-gray-500 mb-2">gemäß Art. 28 DSGVO</p>
          <p className="text-sm text-gray-400 mb-8">Stand: Oktober 2026</p>

          {/* Parteien */}
          <section className="mb-10 grid md:grid-cols-2 gap-4">
            <div className="p-5 bg-gray-50 rounded-xl border border-gray-200">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Auftragsverarbeiter</p>
              <p className="text-sm text-gray-800 leading-relaxed">
                <strong>{LEGAL_OPERATOR_NAME}</strong><br />
                (nachfolgend „Synergic Systems")<br />
                {LEGAL_ADDRESS_STREET}<br />
                {LEGAL_ADDRESS_CITY}, {LEGAL_ADDRESS_COUNTRY}<br />
                <br />
                E-Mail: <a href="mailto:datenschutz@candlear.de" className="text-[#FF6D00] hover:underline">datenschutz@candlear.de</a>
                <br />
                Telefon:{' '}
                <a href={`tel:${SUPPORT_PHONE_TEL}`} className="text-[#FF6D00] hover:underline">
                  {SUPPORT_PHONE_DISPLAY}
                </a>
              </p>
            </div>
            <div className="p-5 bg-gray-50 rounded-xl border border-gray-200">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Verantwortlicher</p>
              <p className="text-sm text-gray-800 leading-relaxed">
                Der im Zuge der Registrierung identifizierte Nutzer<br />
                (nachfolgend „Mandant")
              </p>
            </div>
          </section>

          {/* Präambel */}
          <section className="mb-10">
            <h2 className="text-lg font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              Präambel
            </h2>
            <div className="text-sm text-gray-700 leading-relaxed space-y-3">
              <p>
                Der Verantwortliche nutzt die SaaS-Plattform <strong>Candlear Booking</strong> zur Verwaltung von Terminbuchungen.
                Im Rahmen dieser Nutzung verarbeitet der Auftragsverarbeiter personenbezogene Daten von Endkunden des Verantwortlichen
                (z. B. Namen und E-Mail-Adressen von Personen, die Termine buchen) sowie von Mitarbeitern des Verantwortlichen,
                die dieser als Ressourcen in der Plattform anlegt.
              </p>
              <p>
                Die Parteien schließen diesen Auftragsverarbeitungsvertrag (AVV) gemäß Art. 28 Abs. 3 DSGVO, um die datenschutzrechtliche
                Grundlage für diese Verarbeitung zu schaffen.
              </p>
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl text-sm text-blue-800">
                <strong>Hinweis:</strong> Dieser AVV betrifft ausschließlich die Verarbeitung personenbezogener Daten der
                <em> Endkunden und Mitarbeiter des Verantwortlichen</em> im Rahmen der Terminbuchungs-Plattform. Die Verarbeitung
                personenbezogener Daten des Verantwortlichen selbst (z. B. im Rahmen der Zahlungsabwicklung über Stripe)
                ist <strong>nicht</strong> Gegenstand dieses AVV, sondern richtet sich nach der{' '}
                <a href="/datenschutz" className="underline">Datenschutzerklärung von Candlear Booking</a>.
              </div>
            </div>
          </section>

          <AvvSection para="§ 1" title="Gegenstand und Dauer der Verarbeitung">
            <p><strong>1.1 Gegenstand</strong></p>
            <p className="mt-1">
              Der Auftragsverarbeiter verarbeitet personenbezogene Daten des Verantwortlichen ausschließlich im Rahmen der Bereitstellung
              der Candlear Booking Plattform. Dies umfasst:
            </p>
            <ul className="mt-2 space-y-1 list-disc list-inside text-gray-700">
              <li>Speicherung, Abruf und Löschung von Termindaten</li>
              <li>Versand von Bestätigungs-E-Mails an Endkunden</li>
              <li>Verarbeitung von Chat-Nachrichten zur KI-gestützten Buchungsassistenz</li>
              <li>Verwaltung von Kundenstammdaten</li>
              <li>Verwaltung von Ressourcen (Mitarbeiter des Verantwortlichen) für die Terminplanung</li>
            </ul>
            <p className="mt-4"><strong>1.2 Dauer</strong></p>
            <p className="mt-1">
              Die Verarbeitung beginnt mit der Registrierung des Verantwortlichen und endet mit der Löschung seines Accounts.
              § 7 (Rückgabe und Löschung) bleibt vorbehalten.
            </p>
          </AvvSection>

          <AvvSection para="§ 2" title="Art und Zweck der Verarbeitung">
            <p><strong>Verarbeitungsart:</strong> Erheben, Speichern, Lesen, Übermitteln (E-Mail-Versand), Löschen</p>
            <p className="mt-3"><strong>Verarbeitungszweck:</strong> Bereitstellung eines automatisierten Terminbuchungssystems, einschließlich:</p>
            <ul className="mt-2 space-y-1 list-disc list-inside text-gray-700">
              <li>Entgegennahme und Bestätigung von Buchungsanfragen</li>
              <li>Versand von Bestätigungscodes und Buchungsbestätigungen per E-Mail</li>
              <li>Stornierung und Verwaltung von Terminen</li>
              <li>KI-gestützte Buchungsassistenz via Chat-Widget</li>
            </ul>
          </AvvSection>

          <AvvSection para="§ 3" title="Art der personenbezogenen Daten und Kategorien von Betroffenen">
            <p className="mb-3"><strong>Kategorien betroffener Personen:</strong></p>
            <ul className="mb-3 space-y-1 list-disc list-inside text-gray-700">
              <li>Endkunden des Verantwortlichen (natürliche Personen, die Termine buchen oder das Chat-Widget nutzen)</li>
              <li>Mitarbeiter des Verantwortlichen, die als Ressourcen angelegt sind</li>
            </ul>
            <p className="mb-3"><strong>Verarbeitete Datenkategorien:</strong></p>
            <AvvTable
              headers={['Datenkategorie', 'Konkrete Felder', 'Wo verarbeitet']}
              rows={[
                ['Identifikationsdaten', 'Vor- und Nachname', 'Termindatensatz, Kundenstamm'],
                ['Kontaktdaten', 'E-Mail-Adresse, ggf. Telefonnummer', 'Termindatensatz, E-Mail-Versand'],
                ['Buchungsdaten', 'Datum, Uhrzeit, Termintyp, Ressource', 'Termindatensatz'],
                ['Kommunikationsdaten', 'Chat-Verlauf aus dem Buchungs-Widget', 'Chat-Sessions'],
                ['Technische Daten', 'Temporäre Bestätigungscodes (max. 10 Min.); IP-Adresse und Zugriffszeit bei Aufrufen des Widgets; Empfängeradresse beim E-Mail-Versand', 'Pending Codes, Rate-Limiting, technische Logs'],
                ['Mitarbeiterdaten', 'Name, E-Mail-Adresse, Verfügbarkeiten der als Ressource angelegten Mitarbeiter', 'Konfiguration, Termindatensatz'],
              ]}
            />
            <p className="mt-4">
              Es werden <strong>keine besonderen Kategorien</strong> personenbezogener Daten im Sinne von Art. 9 DSGVO verarbeitet
              (keine Gesundheits-, Religions- oder biometrischen Daten).
            </p>
            <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-800">
              <strong>Hinweis für Arztpraxen, Physiotherapeuten u. ä.:</strong> Wenn Ihre Branche Gesundheitsdaten verarbeitet
              (z. B. Therapieart als Termintyp), handelt es sich um besondere Datenkategorien nach Art. 9 DSGVO.
              Nehmen Sie in diesem Fall Kontakt mit{' '}
              <a href="mailto:datenschutz@candlear.de" className="underline">datenschutz@candlear.de</a> auf –
              ein angepasster AVV ist erforderlich.
            </div>
          </AvvSection>

          <AvvSection para="§ 4" title="Pflichten des Auftragsverarbeiters">
            <p className="mb-4">Der Auftragsverarbeiter verpflichtet sich:</p>

            <div className="space-y-4">
              <AvvClause number="4.1" title="Weisungsgebundenheit">
                Personenbezogene Daten werden ausschließlich auf dokumentierte Weisung des Verantwortlichen verarbeitet –
                auch in Bezug auf die Übermittlung in ein Drittland. Die Weisungen ergeben sich aus diesem AVV, den AGB und den
                Einstellungen, die der Verantwortliche in der Plattform vornimmt; weitere Weisungen erteilt er in Textform.
                Ist der Auftragsverarbeiter nach dem Recht der EU oder eines Mitgliedstaats zu einer anderen Verarbeitung
                verpflichtet, teilt er dem Verantwortlichen diese rechtlichen Anforderungen vor der Verarbeitung mit, sofern
                das betreffende Recht eine solche Mitteilung nicht wegen eines wichtigen öffentlichen Interesses verbietet
                (Art. 28 Abs. 3 lit. a DSGVO). Ist der Auftragsverarbeiter der Ansicht, dass eine Weisung gegen Datenschutzrecht
                verstößt, informiert er den Verantwortlichen unverzüglich.
              </AvvClause>

              <AvvClause number="4.2" title="Vertraulichkeit">
                Alle mit der Verarbeitung befassten Personen sind zur Vertraulichkeit verpflichtet oder unterliegen
                einer gesetzlichen Vertraulichkeitspflicht.
              </AvvClause>

              <AvvClause number="4.3" title="Technische und organisatorische Maßnahmen (TOMs)">
                <p className="mb-3">Der Auftragsverarbeiter implementiert angemessene TOMs gemäß Art. 32 DSGVO:</p>
                <AvvTable
                  headers={['Maßnahme', 'Umsetzung']}
                  rows={[
                    ['Transportverschlüsselung', 'HTTPS/TLS für alle API-Verbindungen'],
                    ['Ruhende Datenverschlüsselung', 'Google Firestore at-rest Encryption'],
                    ['Zugriffskontrolle', 'Firebase Security Rules: Mandantentrennung durch UID-basierte Isolation'],
                    ['Authentifizierung', 'Firebase Authentication mit sicheren Token'],
                    ['Geheimnismanagement', 'Google Secret Manager (keine Credentials im Code)'],
                    ['Verfügbarkeit', 'Google Cloud SLA (europe-west3)'],
                    ['Mandantentrennung', 'Daten verschiedener Mandanten sind technisch isoliert'],
                  ]}
                />
              </AvvClause>

              <AvvClause number="4.4" title="Unterstützung bei Betroffenenrechten">
                Der Auftragsverarbeiter unterstützt den Verantwortlichen bei der Erfüllung von Anfragen betroffener Personen
                (Auskunft, Berichtigung, Löschung) soweit technisch möglich. Datenlöschung und -export werden auf Anfrage
                innerhalb von 30 Tagen bereitgestellt.
              </AvvClause>

              <AvvClause number="4.5" title="Unterstützung bei den Pflichten nach Art. 32 bis 36 DSGVO">
                Der Auftragsverarbeiter unterstützt den Verantwortlichen unter Berücksichtigung der Art der Verarbeitung und
                der ihm zur Verfügung stehenden Informationen bei der Einhaltung der Pflichten aus Art. 32 bis 36 DSGVO:
                Sicherheit der Verarbeitung, Meldung von Datenschutzverletzungen an die Aufsichtsbehörde, Benachrichtigung
                betroffener Personen, Datenschutz-Folgenabschätzung (DSFA) und vorherige Konsultation der Aufsichtsbehörde.
              </AvvClause>

              <AvvClause number="4.6" title="Meldung von Datenpannen">
                Im Falle einer Verletzung des Schutzes personenbezogener Daten informiert der Auftragsverarbeiter den
                Verantwortlichen <strong>unverzüglich, spätestens innerhalb von 36 Stunden</strong> nach Bekanntwerden,
                damit der Verantwortliche die 72-Stunden-Meldefrist gegenüber der Aufsichtsbehörde (Art. 33 DSGVO) einhalten kann.
                Die Meldung enthält, soweit bekannt, die Angaben nach Art. 33 Abs. 3 DSGVO; fehlende Angaben werden nachgereicht.
              </AvvClause>

              <AvvClause number="4.7" title="Verarbeitungsverzeichnis">
                Der Auftragsverarbeiter führt im Rahmen seiner gesetzlichen Pflichten ein Verarbeitungsverzeichnis
                gemäß Art. 30 Abs. 2 DSGVO.
              </AvvClause>
            </div>
          </AvvSection>

          <AvvSection para="§ 5" title="Pflichten des Verantwortlichen">
            <p className="mb-4">Der Verantwortliche verpflichtet sich:</p>
            <div className="space-y-3">
              {[
                'Die Verarbeitung personenbezogener Daten seiner Endkunden auf eine gültige Rechtsgrundlage (Art. 6 DSGVO) zu stützen, bevor er Candlear Booking für die Verarbeitung ihrer Daten einsetzt.',
                'Seinen Endkunden gegenüber eine datenschutzrechtskonforme Datenschutzerklärung bereitzustellen, die die Nutzung von Candlear Booking sowie die Weitergabe an Synergic Systems als Auftragsverarbeiter und die in § 6 genannten Unterauftragsverarbeiter transparent macht.',
                'Den Auftragsverarbeiter unverzüglich zu informieren, wenn er Fehler oder Unregelmäßigkeiten bei der Datenverarbeitung feststellt.',
                'Nur Daten einzugeben oder verarbeiten zu lassen, für die er berechtigt ist. Die Verarbeitung besonderer Kategorien personenbezogener Daten (Art. 9 DSGVO) ist nur nach vorheriger Abstimmung mit dem Auftragsverarbeiter zulässig.',
              ].map((text, i) => (
                <div key={i} className="flex gap-3 text-sm text-gray-700">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-gray-100 border border-gray-200 text-xs font-bold text-gray-500 flex items-center justify-center">{i + 1}</span>
                  <p>{text}</p>
                </div>
              ))}
            </div>
          </AvvSection>

          <AvvSection para="§ 6" title="Unterauftragsverarbeiter">
            <p className="mb-4">
              Der Verantwortliche erteilt hiermit eine <strong>allgemeine Genehmigung</strong> für den Einsatz der
              folgenden Unterauftragsverarbeiter:
            </p>
            <AvvTable
              headers={['Unterauftragsverarbeiter', 'Sitz', 'Zweck', 'Vertragl. Grundlage']}
              rows={[
                ['Google Cloud EMEA Limited / Google Ireland Limited (Firebase, Google Cloud)', 'Irland / EU; Speicherung in europe-west3 (Frankfurt)', 'Datenbankhosting, Cloud Functions, Authentifizierung, Hosting', 'Google Cloud Data Processing Addendum; Weiterübermittlung an Google LLC (USA): EU-US Data Privacy Framework (Art. 45 DSGVO), ergänzend EU-SCC'],
                ['Resend, Inc.', 'USA', 'Versand von Transaktions-E-Mails', 'Resend DPA; EU-US Data Privacy Framework (Art. 45 DSGVO), ergänzend EU-SCC'],
                ['Google Cloud EMEA Limited (Gemini API, kostenpflichtig)', 'Irland; Verarbeitung global, u. a. durch Google LLC (USA)', 'KI-Verarbeitung der Chat-Nachrichten', 'Gemini API Terms, Data Processing Addendum for Products Where Google is a Data Processor; EU-US Data Privacy Framework (Art. 45 DSGVO), ergänzend EU-SCC'],
                ['Functional Software, Inc. (Sentry)', 'USA', 'Fehlerüberwachung', 'Sentry DPA; EU-US Data Privacy Framework (Art. 45 DSGVO), ergänzend EU-SCC'],
              ]}
            />
            <p className="mt-4 text-sm text-gray-600">
              Der Auftragsverarbeiter verpflichtet alle Unterauftragsverarbeiter vertraglich zu gleichwertigen Datenschutzverpflichtungen.
              Kommt ein Unterauftragsverarbeiter seinen Datenschutzpflichten nicht nach, haftet der Auftragsverarbeiter gegenüber dem
              Verantwortlichen für die Einhaltung dieser Pflichten (Art. 28 Abs. 4 DSGVO).
            </p>
            <p className="mt-3 text-sm text-gray-600">
              <strong>Drittländer:</strong> Eine Verarbeitung außerhalb der EU bzw. des EWR erfolgt nur, wenn die Voraussetzungen der
              Art. 44 ff. DSGVO erfüllt sind – derzeit über den Angemessenheitsbeschluss zum EU-US Data Privacy Framework (Art. 45 DSGVO)
              und ergänzend über EU-Standardvertragsklauseln (Art. 46 Abs. 2 lit. c DSGVO).
            </p>
            <p className="mt-3 text-sm text-gray-600">
              <strong>Änderungen:</strong> Der Auftragsverarbeiter informiert den Verantwortlichen über beabsichtigte Änderungen
              an der Liste der Unterauftragsverarbeiter per E-Mail mit einer Vorlauffrist von <strong>30 Tagen</strong>.
              Der Verantwortliche kann Einwände erheben. Kommt keine Einigung zustande, kann der Verantwortliche den Vertrag
              außerordentlich kündigen.
            </p>
          </AvvSection>

          <AvvSection para="§ 7" title="Rückgabe und Löschung von Daten">
            <div className="space-y-4">
              <AvvClause number="7.1" title="Datenexport">
                Der Verantwortliche kann jederzeit über das Admin-Dashboard oder auf Anfrage per E-Mail einen Export seiner
                Daten (Termine, Kundendaten) in maschinenlesbarem Format (JSON) anfordern.
              </AvvClause>
              <AvvClause number="7.2" title="Löschung nach Vertragsende">
                Nach Beendigung des Vertragsverhältnisses löscht der Auftragsverarbeiter alle personenbezogenen Daten des
                Verantwortlichen und seiner Endkunden <strong>innerhalb von 30 Tagen</strong>, sofern keine gesetzliche
                Aufbewahrungspflicht nach dem Recht der EU oder eines Mitgliedstaats besteht. Das gilt auch für Kopien.
                Daten in technischen Logs und systembedingten Sicherungen der Dienstleister werden mit deren regulärem
                Ablauf gelöscht (technische Logs spätestens nach 30 Tagen) und bis dahin nicht mehr verwendet.
              </AvvClause>
              <AvvClause number="7.3" title="Selbstlöschung">
                Der Verantwortliche kann seinen Account und alle zugehörigen Daten jederzeit eigenständig im Admin-Dashboard
                unter <strong>Einstellungen → Datenschutz → Account löschen</strong> löschen.
              </AvvClause>
            </div>
          </AvvSection>

          <AvvSection para="§ 8" title="Kontrollrechte des Verantwortlichen">
            <div className="space-y-3 text-sm text-gray-700">
              <p>
                <strong>8.1</strong> Der Verantwortliche hat das Recht, die Einhaltung dieses AVV zu überprüfen durch:
              </p>
              <ul className="list-disc list-inside ml-4 space-y-1">
                <li>Anforderung von Dokumenten und Nachweisen (schriftlich)</li>
                <li>Inspektion vor Ort (mit 14 Tagen Vorankündigung, maximal einmal jährlich)</li>
              </ul>
              <p>
                <strong>8.2</strong> Der Auftragsverarbeiter stellt alle erforderlichen Informationen zum Nachweis der Einhaltung
                dieses Vertrags zur Verfügung.
              </p>
            </div>
          </AvvSection>

          <AvvSection para="§ 9" title="Haftung">
            <div className="space-y-3 text-sm text-gray-700">
              <p>
                <strong>9.1</strong> Für die Haftung der Parteien untereinander gilt die Haftungsregelung in{' '}
                <a href="/agb" className="text-[#FF6D00] hover:underline">§ 9 der AGB</a>.
              </p>
              <p>
                <strong>9.2</strong> Die Haftung gegenüber betroffenen Personen nach Art. 82 DSGVO bleibt unberührt.
              </p>
            </div>
          </AvvSection>

          <AvvSection para="§ 10" title="Anwendbares Recht und Gerichtsstand">
            <p>
              Dieser Vertrag unterliegt deutschem Recht. Ist der Verantwortliche Kaufmann, juristische Person des öffentlichen
              Rechts oder öffentlich-rechtliches Sondervermögen, ist ausschließlicher Gerichtsstand Köln (§ 38 Abs. 1 ZPO).
            </p>
          </AvvSection>

          <AvvSection para="§ 11" title="Kontakt">
            <div className="p-4 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-700">
              Datenschutzanfragen und Weisungen des Verantwortlichen sind zu richten an:<br /><br />
              <strong>{LEGAL_OPERATOR_NAME}</strong><br />
              E-Mail: <a href="mailto:datenschutz@candlear.de" className="text-[#FF6D00] hover:underline">datenschutz@candlear.de</a>
              <br />
              Telefon:{' '}
              <a href={`tel:${SUPPORT_PHONE_TEL}`} className="text-[#FF6D00] hover:underline">
                {SUPPORT_PHONE_DISPLAY}
              </a>
              <br />
              {LEGAL_ADDRESS_STREET}, {LEGAL_ADDRESS_CITY}
              <p className="mt-3 text-xs text-gray-600">
                Formale Weisungen im Sinne dieses AVV sollten zur Nachvollziehbarkeit bevorzugt per E-Mail erfolgen.
              </p>
            </div>
          </AvvSection>

          {/* Annahme */}
          <section className="mt-10 p-6 bg-[#FF6D00]/5 border border-[#FF6D00]/20 rounded-2xl">
            <h2 className="text-base font-bold text-gray-900 mb-3" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              Annahme dieses Vertrags
            </h2>
            <p className="text-sm text-gray-700 leading-relaxed">
              Durch die Registrierung bei Candlear Booking und die Bestätigung der Checkbox{' '}
              <strong>„… und stimme dem Auftragsverarbeitungsvertrag (AVV) zu"</strong> erklärt der Verantwortliche
              sein Einverständnis mit diesem AVV. Der Auftragsverarbeitungsvertrag gilt ab dem Zeitpunkt der
              Registrierung als wirksam geschlossen.
            </p>
          </section>

          {/* TOMs Anlage */}
          <section className="mt-10">
            <h2 className="text-lg font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              Anlage 1: Technische und organisatorische Maßnahmen (TOMs)
            </h2>
            <p className="text-sm text-gray-600 mb-6">Diese Anlage konkretisiert die nach Art. 32 DSGVO getroffenen Maßnahmen.</p>

            {[
              {
                title: 'Vertraulichkeit',
                rows: [
                  ['Zugangskontrolle', 'Firebase Authentication; kein öffentlicher Zugriff auf Admin-Bereiche'],
                  ['Zugriffskontrolle', 'Mandantentrennung: Jeder Nutzer sieht ausschließlich eigene Daten (Firestore Security Rules, UID-basiert)'],
                  ['Trennungskontrolle', 'Separate Datenbankdokumente pro Mandant (users/{uid}/...)'],
                  ['Pseudonymisierung', 'API-Keys als Zugangsmittel statt Klarnamen'],
                  ['Zwei-Faktor-Anmeldung', 'Anmeldung mit E-Mail und Passwort nur mit zusätzlichem Einmalcode per E-Mail (erneut spätestens nach 30 Tagen); bei Google-Login gilt die Anmeldesicherheit des Google-Kontos'],
                  ['Missbrauchsschutz', 'Rate-Limiting für API, Widget und Code-Eingaben'],
                ],
              },
              {
                title: 'Integrität',
                rows: [
                  ['Übertragungskontrolle', 'HTTPS/TLS für alle Verbindungen (erzwungen durch Firebase Hosting)'],
                  ['Eingabekontrolle', 'Server-seitige Validierung aller API-Eingaben'],
                ],
              },
              {
                title: 'Verfügbarkeit',
                rows: [
                  ['Verfügbarkeitskontrolle', 'Google Cloud SLA 99,9% für Firestore und Cloud Functions'],
                  ['Redundanz', 'Firestore speichert Daten redundant über mehrere Zonen der Region europe-west3'],
                ],
              },
              {
                title: 'Belastbarkeit',
                rows: [
                  ['Skalierbarkeit', 'Serverless Cloud Functions (automatische Skalierung)'],
                  ['Region', 'Firestore und Cloud Functions in europe-west3 (Frankfurt); Chat, E-Mail und Zahlungen siehe Unterauftragsverarbeiter in § 6'],
                ],
              },
              {
                title: 'Organisation und Überprüfung',
                rows: [
                  ['Personal', 'Einzelunternehmen: Zugriff auf Produktivsysteme und Kundendaten hat ausschließlich der Inhaber'],
                  ['Unterauftragsverarbeiter', 'Auswahl nach Datenschutzniveau, Abschluss von Verträgen nach Art. 28 DSGVO (siehe § 6)'],
                  ['Löschkonzept', 'Automatische Löschung von Bestätigungscodes (10 Min.), Chat-Sessions (24 Std.), Dashboard-Benachrichtigungen (90 Tage), technischen Logs (max. 30 Tage) sowie aller Daten eines Mandanten 30 Tage nach Vertragsende'],
                  ['Überprüfung', 'Überprüfung der Maßnahmen bei wesentlichen Änderungen der Plattform, mindestens einmal jährlich'],
                ],
              },
            ].map(section => (
              <div key={section.title} className="mb-6">
                <h3 className="text-sm font-semibold text-gray-700 mb-2">{section.title}</h3>
                <AvvTable headers={['Maßnahme', 'Details']} rows={section.rows} />
              </div>
            ))}
          </section>

        </div>
      </div>
    </div>
  );
}

function AvvSection({ para, title, children }: { para: string; title: string; children: React.ReactNode }) {
  return (
    <section className="mb-10">
      <h2 className="text-lg font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200 flex items-baseline gap-2" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
        <span className="text-[#FF6D00] font-mono text-sm">{para}</span>
        {title}
      </h2>
      <div className="text-sm text-gray-700 leading-relaxed space-y-2">
        {children}
      </div>
    </section>
  );
}

function AvvClause({ number, title, children }: { number: string; title: string; children: React.ReactNode }) {
  return (
    <div className="text-sm text-gray-700">
      <p className="font-semibold text-gray-800 mb-1">{number} {title}</p>
      <div className="leading-relaxed">{children}</div>
    </div>
  );
}

function AvvTable({ headers, rows }: { headers: string[]; rows: string[][] }) {
  return (
    <div className="overflow-x-auto rounded-xl border border-gray-200">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-50 border-b border-gray-200">
            {headers.map(h => (
              <th key={h} className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((row, i) => (
            <tr key={i} className="hover:bg-gray-50 transition-colors">
              {row.map((cell, j) => (
                <td key={j} className="px-4 py-2.5 text-gray-700">{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
